import {
  Button,
  createTheme,
  FileInput,
  MantineProvider,
  Modal,
  MultiSelect,
  NumberInput,
  PasswordInput,
  ScrollArea,
  Select,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { ReactNode } from "react";

const MantineGlobalLayout = ({ children }: { children: ReactNode }) => {
  const theme = createTheme({
    defaultRadius: "sm",
    radius: {
      xs: "2px",
      sm: "4px",
      md: "6px",
      lg: "8px",
      xl: "12px",
      max: "16px",
    },
    colors: {
      primary: [
        "#F3F9FD", // BLUE_100
        "#E3EEF8", // BLUE_200
        "#BFD8EE", // BLUE_300
        "#A8C8E4", // interpolated
        "#6FA3D1", // BLUE_400
        "#1F68A8", // BLUE_500 — main brand
        "#0B4A7C", // BLUE_600
        "#093C65", // BLUE_700
        "#003A61", // BLUE_800
        "#002545", // darkest
      ],
      slate: [
        "#FFFFFF", // NEUTRAL_100
        "#F5F5F5", // NEUTRAL_200
        "#EBEBEB", // NEUTRAL_300
        "#D8D8D8", // interpolated
        "#BFBFBF", // NEUTRAL_400
        "#7D7D7D", // NEUTRAL_500
        "#666666", // NEUTRAL_600
        "#333333", // NEUTRAL_700
        "#262626", // interpolated
        "#1A1A1A", // NEUTRAL_900
      ],
    },
    primaryColor: "primary",
    primaryShade: 5,
    fontFamily: "Figtree, sans-serif",
    components: {
      Text: Text.extend({
        defaultProps: {
          c: "slate.7",
        },
      }),
      Title: Title.extend({
        defaultProps: {
          c: "slate.8",
          fw: 600,
        },
      }),
      Modal: Modal.extend({
        defaultProps: {
          radius: "lg",
          centered: true,
          scrollAreaComponent: ScrollArea.Autosize,
          closeOnEscape: false,
          closeOnClickOutside: false,
          overlayProps: {
            backgroundOpacity: 0.6,
            blur: 3,
          },
        },
      }),
      TextInput: TextInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      PasswordInput: PasswordInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      Textarea: Textarea.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      FileInput: FileInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),

      NumberInput: NumberInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      Select: Select.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      MultiSelect: MultiSelect.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
          descriptionProps: {
            c: "slate.5",
            fz: "12px",
          },
        },

        styles: () => ({
          // Pill container
          pill: {
            backgroundColor: "var(--mantine-color-primary-1)",
            color: "var(--mantine-color-primary-7)",
            fontWeight: 600,
          },
        }),
      }),

      Button: Button.extend({
        defaultProps: {
          fw: 600,
          fz: "15px",
          size: "md",
          radius: "md",
        },
      }),
      Badge: {
        defaultProps: {
          radius: "sm",
          fw: 600,
        },
      },
    },
  });

  return <MantineProvider theme={theme}>{children}</MantineProvider>;
};

export default MantineGlobalLayout;
